import { Link } from "react-router-dom";
import { useSetAtom } from "jotai";
import { Button, Icon } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useAuth } from "@/hooks/useAuth";
import { useCanShop } from "@/hooks/useCanShop";
import { isMobileDrawerOpenAtom } from "./store/drawerAtoms";
import { isSearchPanelOpenAtom } from "@customer/Search/store/searchAtoms";
import { ShipToSwitcher } from "./_components/ShipToSwitcher";

interface MobileNavProps {
    cartCount: number;
}

export const MobileNav = ({ cartCount }: MobileNavProps) => {
    const { isAuthenticated } = useAuth();
    const setDrawerOpen = useSetAtom(isMobileDrawerOpenAtom);
    const openSearchPanel = useSetAtom(isSearchPanelOpenAtom);

    // Not-yet-approved users are walled at /c/pending; hide the shopping entry
    // points and keep the logo on the wall so tapping it doesn't bounce.
    const canShop = useCanShop();

    return (
        <div
            data-testid="mobile-nav"
            className="flex md:hidden w-full items-center"
        >
            <Link
                to={
                    isAuthenticated
                        ? canShop
                            ? ROUTES.BROWSE
                            : ROUTES.PENDING
                        : ROUTES.HOME
                }
                aria-label="USNARA home"
                className="flex items-center"
            >
                <img
                    src="/logo/Logotype-Color.svg"
                    alt=""
                    className="h-8 w-auto"
                />
            </Link>
            {/* Directly right of the logo, per the Account Dashboard mockup. Only a shopper
                who can shop has a destination to ship to. */}
            {canShop && <ShipToSwitcher compact />}
            <div className="flex-1" />
            <div className="flex items-center gap-1">
                {isAuthenticated ? (
                    canShop && (
                        <>
                            <button
                                type="button"
                                aria-label="Search"
                                onClick={() => openSearchPanel(true)}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <Icon name="search" className="text-2xl" />
                            </button>
                            <Link
                                to={ROUTES.CART}
                                aria-label="Cart"
                                className="btn btn-ghost btn-sm btn-square relative"
                            >
                                <Icon
                                    name="shopping_cart"
                                    className="text-2xl"
                                />
                                {cartCount > 0 && (
                                    <span
                                        data-testid="cart-badge"
                                        className="badge badge-warning badge-sm absolute -top-1 -right-1"
                                    >
                                        {cartCount}
                                    </span>
                                )}
                            </Link>
                        </>
                    )
                ) : (
                    /* TODO: /apply arrives in E5-S4 (#191) */
                    <Button
                        to={ROUTES.APPLY}
                        color={ComponentColor.Warning}
                        size={ComponentSize.SM}
                        className="text-neutral font-bold normal-case"
                    >
                        Apply Now
                    </Button>
                )}
                <button
                    type="button"
                    aria-label="Open menu"
                    onClick={() => setDrawerOpen(true)}
                    className="btn btn-ghost btn-sm btn-square"
                >
                    <Icon name="menu" className="text-2xl" />
                </button>
            </div>
        </div>
    );
};
